import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, ListGroup, Badge, Form, InputGroup, Button, Spinner, Alert, Pagination, Modal } from 'react-bootstrap';
import { FaSearch, FaFilter, FaUserPlus, FaUsers, FaCheckCircle, FaShieldAlt, FaTrash, FaEye } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import userService from '../services/userService';

function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [newUsersCount, setNewUsersCount] = useState(0);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'subscriber'
  });

  const itemsPerPage = 10;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: 'created_at',
        sort_order: 'desc',
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (roleFilter) {
        params.role = roleFilter;
      }

      const data = await userService.getAll(params);
      setUsers(data.users);
      setTotalUsers(data.total);
      setTotalPages(data.pages);

      // Fetch counts for stats cards
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      const allUsersData = await userService.getAll({ per_page: 1000 }); // Get all for stats
      const allUsers = allUsersData.users || [];
      const newUsers = allUsers.filter(u => new Date(u.created_at) > monthAgo);
      const verified = allUsers.filter(u => u.email_verified);
      setNewUsersCount(newUsers.length);
      setVerifiedCount(verified.length);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenModal = () => {
    setFormData({ username: '', email: '', password: '', role: 'subscriber' });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ username: '', email: '', password: '', role: 'subscriber' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await userService.create(formData);
      handleCloseModal();
      fetchUsers();
    } catch (err) {
      setError('Failed to create user');
      console.error(err);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await userService.delete(userToDelete.id);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const retentionRate = totalUsers > 0 ? Math.round((verifiedCount / totalUsers) * 100) : 0;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'primary',
      seller: 'secondary',
      subscriber: 'light'
    };
    return variants[role] || 'light';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Users"
        subtitle={`${totalUsers} total users • ${newUsersCount} new this month`}
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-primary bg-opacity-10 rounded me-3">
                  <FaUsers className="text-primary" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Total Users</p>
                  <h4 className="fw-bold mb-0">{totalUsers}</h4>
                  <small className="text-muted">{newUsersCount} new this month</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-success bg-opacity-10 rounded me-3">
                  <FaCheckCircle className="text-success" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Email Verified</p>
                  <h4 className="fw-bold mb-0">{retentionRate}%</h4>
                  <small className="text-muted">{verifiedCount} verified users</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-danger bg-opacity-10 rounded me-3">
                  <FaShieldAlt className="text-danger" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Admins</p>
                  <h4 className="fw-bold mb-0">{users.filter((u) => u.role === 'admin').length}</h4>
                  <small className="text-muted">Admin accounts</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col md={8}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaSearch className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search users by username or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
        </Col>
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaFilter className="text-muted" />
            </InputGroup.Text>
            <Form.Select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="seller">Seller</option>
              <option value="subscriber">Subscriber</option>
            </Form.Select>
          </InputGroup>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="fw-semibold mb-1">All Users</h5>
              <p className="text-muted mb-0 small">Manage user accounts and roles</p>
            </div>
            <Button variant="primary" onClick={() => handleOpenModal()}>
              <FaUserPlus className="me-2" />
              Add User
            </Button>
          </div>

          <ListGroup variant="flush">
            {users.map((user) => (
              <ListGroup.Item key={user.id} className="px-0 py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold me-3"
                      style={{ width: '48px', height: '48px' }}
                    >
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="fw-semibold">{user.username}</div>
                      <small className="text-muted">{user.email}</small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex gap-2">
                      <Badge bg={getRoleBadge(user.role)} pill className="text-dark">
                        {user.role}
                      </Badge>
                      <Badge bg={user.email_verified ? 'success' : 'warning'} pill className="bg-opacity-25 text-dark">
                        {user.email_verified ? 'Verified' : 'Unverified'}
                      </Badge>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1"
                        onClick={() => navigate(`/users/${user.id}`)}
                        title="View Details"
                      >
                        <FaEye />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1"
                        onClick={() => handleDeleteClick(user)}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} disabled />;
              }
              return null;
            })}

            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="subscriber">Subscriber</option>
                <option value="seller">Seller</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ConfirmationModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete user "${userToDelete?.username}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />
    </Container>
  );
}

export default UsersPage;
